import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api';

const ACCENT = '#2B7FFF';

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
}

function formatTime(timeStr) {
  if (!timeStr) return '';
  const [h, m] = timeStr.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hour = h % 12 || 12;
  return `${hour}:${String(m).padStart(2, '0')} ${ampm}`;
}

export default function CancelAppointment() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [appt, setAppt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get(`/appointments/cancel/${token}`)
      .then(r => setAppt(r.data))
      .catch(err => setError(err.response?.data?.error || 'Invalid or expired cancel link.'))
      .finally(() => setLoading(false));
  }, [token]);

  const handleCancel = async () => {
    setCancelling(true);
    setError('');
    try {
      await api.post(`/appointments/cancel/${token}`);
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not cancel. Please try again.');
    } finally {
      setCancelling(false);
    }
  };

  const containerStyle = {
    maxWidth: '480px',
    margin: '0 auto',
    padding: '64px 24px',
    textAlign: 'center',
    minHeight: 'calc(100vh - 60px)',
    background: 'transparent',
  };

  if (loading) {
    return (
      <div style={containerStyle}>
        <div style={{ color: '#555', fontSize: '14px' }}>Loading...</div>
      </div>
    );
  }

  if (done) {
    return (
      <div style={containerStyle}>
        <div style={{ fontSize: '48px', marginBottom: '20px' }}>✓</div>
        <h1 style={{ fontSize: '24px', fontWeight: 900, marginBottom: '12px' }}>Appointment Cancelled</h1>
        <p style={{ color: '#888', fontSize: '15px', marginBottom: '32px' }}>
          Your appointment has been cancelled. No charge was applied.
        </p>
        <button
          onClick={() => navigate('/book')}
          className="btn btn-primary"
          style={{ padding: '14px 32px' }}
        >
          Book Again
        </button>
      </div>
    );
  }

  if (error && !appt) {
    return (
      <div style={containerStyle}>
        <div style={{ fontSize: '48px', marginBottom: '20px' }}>⚠️</div>
        <h1 style={{ fontSize: '22px', fontWeight: 900, marginBottom: '12px' }}>Link Invalid</h1>
        <p style={{ color: '#888', fontSize: '15px', marginBottom: '32px' }}>{error}</p>
        <button onClick={() => navigate('/book')} className="btn btn-primary" style={{ padding: '14px 32px' }}>
          Book New Appointment
        </button>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <div style={{ color: ACCENT, fontSize: '11px', fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', marginBottom: '12px' }}>
        MANE
      </div>
      <h1 style={{ fontSize: '26px', fontWeight: 900, textTransform: 'uppercase', marginBottom: '8px' }}>
        Cancel Appointment
      </h1>

      {/* Appointment card */}
      <div style={{
        background: '#0F1929', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '12px',
        padding: '24px', margin: '32px 0', textAlign: 'left',
      }}>
        <div style={{ color: '#555', fontSize: '11px', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '12px' }}>
          Your Appointment
        </div>
        <div style={{ fontSize: '18px', fontWeight: 800, marginBottom: '6px' }}>{appt.service_name}</div>
        <div style={{ color: '#aaa', fontSize: '14px', marginBottom: '4px' }}>{formatDate(appt.appointment_date)}</div>
        <div style={{ color: '#aaa', fontSize: '14px', marginBottom: '16px' }}>{formatTime(appt.start_time)}</div>
        <div style={{ color: ACCENT, fontSize: '16px', fontWeight: 700 }}>${parseFloat(appt.final_price).toFixed(2)}</div>
      </div>

      {appt.can_cancel ? (
        <>
          <p style={{ color: '#888', fontSize: '14px', marginBottom: '28px', lineHeight: 1.7 }}>
            You're cancelling more than 3 hours before your appointment — <strong style={{ color: '#fff' }}>no charge will be applied.</strong>
          </p>
          {error && (
            <div style={{
              color: '#ef4444', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)',
              padding: '12px 16px', borderRadius: '6px', fontSize: '14px', marginBottom: '16px',
            }}>
              {error}
            </div>
          )}
          <button
            onClick={handleCancel}
            disabled={cancelling}
            style={{
              width: '100%', padding: '16px', background: 'transparent',
              border: '1.5px solid rgba(239,68,68,0.5)', color: '#ef4444',
              borderRadius: '8px', fontSize: '15px', fontWeight: 800,
              cursor: cancelling ? 'not-allowed' : 'pointer', marginBottom: '12px',
              letterSpacing: '0.04em', textTransform: 'uppercase',
            }}
          >
            {cancelling ? 'Cancelling...' : 'Yes, Cancel My Appointment'}
          </button>
          <button
            onClick={() => navigate('/')}
            style={{
              width: '100%', padding: '14px', background: 'transparent',
              border: '1px solid #2a2a2a', color: '#555',
              borderRadius: '8px', fontSize: '14px', cursor: 'pointer',
            }}
          >
            Never Mind, Keep It
          </button>
        </>
      ) : (
        <>
          <div style={{
            background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)',
            borderRadius: '10px', padding: '20px', marginBottom: '28px',
          }}>
            <div style={{ color: '#ef4444', fontWeight: 800, fontSize: '15px', marginBottom: '8px' }}>
              ⚠️ Too Late to Cancel
            </div>
            <p style={{ color: '#aaa', fontSize: '14px', margin: 0, lineHeight: 1.7 }}>
              Your appointment is in <strong style={{ color: '#fff' }}>{appt.hours_until} hours</strong>.
              Cancellations within 3 hours are not allowed. Please show up or contact Christian directly.
            </p>
          </div>
          <button onClick={() => navigate('/')} className="btn btn-ghost" style={{ width: '100%', padding: '14px' }}>
            Go Back
          </button>
        </>
      )}
    </div>
  );
}
